import React, { useState } from 'react';
import { ExternalLink, Trophy, Medal, GraduationCap, Shield, Award, Code, Bot } from 'lucide-react';
import { portfolioData } from '../mockData';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  trophy: Trophy,
  medal: Medal,
  'graduation-cap': GraduationCap,
  shield: Shield,
  award: Award,
  code: Code,
  bot: Bot
};

const Home = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus('');
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        setFormStatus('Message sent successfully! Will get back to you soon.');
        setFormData({ name: '', email: '', message: '' });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setFormStatus('Failed to send message. Please try again or email directly.');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => {
        setFormStatus('');
      }, 5000);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="bg-black text-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-black/95 backdrop-blur-sm z-50 border-b-2 border-lime-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="text-2xl font-black tracking-wider text-lime-500">
              PETE McGRAW
            </div>
            <div className="hidden md:flex space-x-8">
              <a href="#about" className="text-white hover:text-lime-500 font-bold uppercase tracking-wide transition-colors">
                About
              </a>
              <a href="#ventures" className="text-white hover:text-lime-500 font-bold uppercase tracking-wide transition-colors">
                Ventures
              </a>
              <a href="/web-portfolio" className="text-white hover:text-lime-500 font-bold uppercase tracking-wide transition-colors">
                Portfolio
              </a>
              <a href="#achievements" className="text-white hover:text-lime-500 font-bold uppercase tracking-wide transition-colors">
                Achievements
              </a>
              <a href="#contact" className="text-white hover:text-lime-500 font-bold uppercase tracking-wide transition-colors">
                Contact
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(82, 255, 0, 0.03) 10px, rgba(82, 255, 0, 0.03) 20px)'
        }}></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block bg-lime-500 text-black px-4 py-2 font-black text-sm tracking-widest mb-6 transform -skew-x-12">
                <span className="inline-block transform skew-x-12">SERIAL ENTREPRENEUR</span>
              </div>
              <h1 className="text-6xl md:text-8xl font-black mb-6 uppercase leading-none">
                <span className="text-white">PETE</span>
                <br />
                <span className="text-lime-500 block" style={{ textShadow: '0 0 20px rgba(82, 255, 0, 0.5)' }}>McGRAW</span>
              </h1>
              <div className="text-xl md:text-2xl font-bold text-gray-300 mb-8 uppercase tracking-wide">
                {portfolioData.hero.tagline}
              </div>
              <p className="text-2xl md:text-3xl font-black text-lime-500 mb-12 uppercase">
                {portfolioData.hero.headline}
              </p>
              <a 
                href="#contact" 
                className="inline-block bg-lime-500 text-black px-8 py-4 font-black text-lg uppercase tracking-wider hover:bg-white transition-all transform hover:scale-105 active:scale-95"
              >
                Connect With Pete
              </a>
            </div>
            
            <div className="relative">
              <div className="relative w-full max-w-md mx-auto">
                <div className="absolute inset-0 bg-lime-500 blur-3xl opacity-20"></div>
                <div className="relative aspect-square rounded-full overflow-hidden border-4 border-lime-500 shadow-2xl">
                  <img 
                    src={portfolioData.hero.image} 
                    alt="Pete McGraw" 
                    className="w-full h-full object-cover object-center scale-125"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Diagonal Separator */}
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-lime-500 transform origin-bottom-left -skew-y-2"></div>
      </section>

      {/* About Section */}
      <section id="about" className="relative py-32 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <h2 className="text-5xl md:text-7xl font-black uppercase mb-6">
              <span className="text-lime-500">{portfolioData.about.title}</span>
            </h2>
            <div className="h-2 w-32 bg-lime-500"></div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {portfolioData.about.stats.map((stat, index) => (
              <div key={index} className="bg-gray-900 border-2 border-lime-500 p-8 text-center transform hover:scale-105 transition-transform">
                <div className="text-6xl font-black text-lime-500 mb-2" style={{ textShadow: '0 0 20px rgba(82, 255, 0, 0.5)' }}>
                  {stat.number}
                </div>
                <div className="text-lg font-bold text-white uppercase tracking-wide">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
          
          <div className="bg-gray-900 border-l-8 border-lime-500 p-8 md:p-12">
            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
              {portfolioData.about.description}
            </p>
          </div>
        </div>
      </section>

      {/* Ventures Section */}
      <section id="ventures" className="relative py-32 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <h2 className="text-5xl md:text-7xl font-black uppercase mb-6">
              <span className="text-white">PETE'S</span> <span className="text-lime-500">VENTURES</span>
            </h2>
            <div className="h-2 w-32 bg-lime-500"></div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {portfolioData.ventures.map((venture, index) => (
              <div 
                key={index} 
                className="bg-black border-2 border-gray-700 hover:border-lime-500 p-6 group transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="text-xs font-bold text-lime-500 uppercase tracking-wider mb-2">
                      {venture.category}
                    </div>
                    <h3 className="text-2xl font-black text-white uppercase mb-2 group-hover:text-lime-500 transition-colors">
                      {venture.name}
                    </h3>
                  </div>
                  {venture.url && (
                    <a 
                      href={venture.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-lime-500 transition-colors"
                    >
                      <ExternalLink className="w-5 h-5" />
                    </a>
                  )}
                </div>
                {venture.role && (
                  <div className="text-sm font-bold text-lime-500 uppercase mb-4 tracking-wide">
                    {venture.role}
                  </div>
                )}
                {venture.status && (
                  <div className="inline-block border border-lime-500 text-lime-500 text-xs font-bold uppercase tracking-wider px-2 py-1 mb-4">
                    {venture.status}
                  </div>
                )}
                <p className="text-gray-400 text-sm leading-relaxed">
                  {venture.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section id="achievements" className="relative py-32 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <h2 className="text-5xl md:text-7xl font-black uppercase mb-6">
              <span className="text-lime-500">ACHIEVEMENTS</span>
            </h2>
            <div className="h-2 w-32 bg-lime-500"></div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {portfolioData.achievements.map((achievement, index) => {
              const Icon = iconMap[achievement.icon] || Trophy;
              return (
                <div 
                  key={index} 
                  className="bg-gray-900 border-2 border-lime-500 p-8 group hover:bg-lime-500 transition-all duration-300 transform hover:scale-105"
                >
                  <div className="text-lime-500 group-hover:text-black transition-colors mb-4">
                    <Icon className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-black text-white group-hover:text-black uppercase mb-3 transition-colors">
                    {achievement.title}
                  </h3>
                  <p className="text-gray-300 group-hover:text-black text-sm transition-colors mb-4">
                    {achievement.description}
                  </p>
                  {achievement.links && achievement.links.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {achievement.links.map((link, linkIndex) => (
                        <a 
                          key={linkIndex}
                          href={link.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-lime-500 group-hover:text-black font-bold text-xs uppercase transition-colors bg-black group-hover:bg-white px-3 py-1 rounded"
                        >
                          {link.name}
                        </a>
                      ))}
                    </div>
                  )}
                  {achievement.link && !achievement.links && (
                    <a 
                      href={achievement.link} 
                      target={achievement.link.startsWith('http') ? "_blank" : "_self"}
                      rel={achievement.link.startsWith('http') ? "noopener noreferrer" : undefined}
                      className="inline-flex items-center gap-2 text-lime-500 group-hover:text-black font-bold text-sm mt-4 transition-colors"
                    >
                      View <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>
              );
            })}
          </div>

          {/* Skills */}
          <div className="mt-20">
            <h3 className="text-4xl font-black text-white uppercase mb-8">
              Core <span className="text-lime-500">Competencies</span>
            </h3>
            <div className="flex flex-wrap gap-4">
              {portfolioData.skills.map((skill, index) => (
                <div 
                  key={index}
                  className="bg-gray-900 border-2 border-lime-500 px-6 py-3 font-bold text-white hover:bg-lime-500 hover:text-black transition-all uppercase tracking-wide text-sm"
                >
                  {skill}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="relative py-32 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16 text-center">
            <h2 className="text-5xl md:text-7xl font-black uppercase mb-6">
              <span className="text-white">GET IN</span> <span className="text-lime-500">TOUCH</span>
            </h2>
            <div className="h-2 w-32 bg-lime-500 mx-auto"></div>
            <p className="text-xl text-gray-300 mt-8 font-bold uppercase">
              Ready to connect with Pete?
            </p>
          </div>
          
          <div className="bg-black border-4 border-lime-500 p-8 md:p-12">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-black uppercase tracking-wider text-lime-500 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-900 border-2 border-gray-700 focus:border-lime-500 text-white px-4 py-3 outline-none transition-colors font-medium"
                  placeholder="Your name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-black uppercase tracking-wider text-lime-500 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-900 border-2 border-gray-700 focus:border-lime-500 text-white px-4 py-3 outline-none transition-colors font-medium"
                  placeholder="your@email.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-black uppercase tracking-wider text-lime-500 mb-2">
                  Message
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="w-full bg-gray-900 border-2 border-gray-700 focus:border-lime-500 text-white px-4 py-3 outline-none transition-colors font-medium resize-none"
                  placeholder="What's on your mind?"
                />
              </div>
              
              {formStatus && (
                <div className="bg-lime-500 text-black px-4 py-3 font-bold text-center uppercase">
                  {formStatus}
                </div>
              )}
              
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-lime-500 text-black px-8 py-4 font-black text-lg uppercase tracking-wider hover:bg-white transition-all transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t-2 border-lime-500 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-3xl font-black text-lime-500 mb-4 uppercase">
              Pete McGraw
            </div>
            <p className="text-gray-400 font-medium uppercase tracking-wide text-sm">
              Building the future. One venture at a time.
            </p>
            <div className="mt-6 text-xs text-gray-600 uppercase tracking-wider">
              © 2025 Pete McGraw. All Rights Reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
